<template>
  <div class="flex h-screen dark:bg-gray-900" :class="isAuthenticated ? 'bg-gray-100' : 'bg-slate-200'">
    <!-- Top Navigation Bar -->
    <div v-if="isAuthenticated" class="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
      <div class="flex items-center justify-between h-16 px-4 md:px-6">
        <!-- Left side: Logo and toggle -->
        <div class="flex items-center">
          <button @click="toggleSidebar" class="p-2  hidden md:block mr-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors md:hidden">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <!-- Logo Esquerda -->
                      <div class="flex items-center">
            <img v-if="clinicStore.clinic.logo" :src="clinicStore.clinic.logo + '?' + Date.now()" alt="Logo Clínica" class="h-10 w-auto mr-3 object-contain" @error="handleLogoError" />
            <img v-else src="@/assets/logo_ccdti.png" alt="Logo CCDTI" class="h-10 w-auto mr-3 object-contain" />
          </div>
        </div>


        <!-- Page Title (visible on mobile) -->
        <h2 class="text-lg font-semibold text-gray-800 dark:text-white md:hidden">
          {{ pageTitle }}
        </h2>

        <!-- Right side: Notifications and Profile -->
        <div class="flex items-center space-x-3" :style="{ color: 'var(--clinic-icon-color)' }">
          <!-- Notifications -->
          <!--
          <div class="relative" data-notification-menu>
            <NotificationsPage 
              @open-full-history="navigateToNotificationsHistory"
              :is-collapsed="false"
              :are-notifications-enabled="areNotificationsEnabled"
              class="flex items-center "
            />
          </div>
          -->

          <!-- User Profile -->
          <div class="relative" data-user-menu>
            <button
              @click="toggleUserMenu"
              class="flex items-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 p-1 transition-all duration-200"
              data-user-menu-button
            >
              <!-- Exibe as iniciais se não tiver imagem -->
              <div v-if="!profileImage" 
                class="h-8 w-8 rounded-full font-bold flex items-center justify-center flex-shrink-0 bg-gray-300 text-gray-800 dark:bg-gray-600 dark:text-gray-200"
              >
                {{ getUserInitials }}
              </div>

              <!-- Exibe imagem se houver -->
              <img
                v-else
                :src="profileImage"
                :alt="getUserName"
                @error="handleImageError"
                class="h-8 w-8 rounded-full object-cover flex-shrink-0"
              />
              
              <span class="ml-2 font-medium text-gray-800 dark:text-gray-200 hidden md:block">{{ getUserName }}</span>
              <component
                :is="isUserMenuOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                class="h-4 w-4 ml-1 flex-shrink-0 text-gray-800 dark:text-gray-200 hidden md:block"
              />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Sidebar -->
    <div v-if="isAuthenticated">
<aside :class="[
  'transition-all duration-300 ease-out',
  'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200',
  
  // Lógica de visibilidade e posicionamento
  'fixed inset-y-0 left-0 z-40',
  'border-r border-gray-200 dark:border-gray-700',
  
  // --- ALTERAÇÃO PRINCIPAL AQUI ---
  // Esconde no mobile e exibe como 'flex' a partir de telas médias (md)
  'hidden md:flex flex-col',

  // A largura continua controlada pela variável 'isCollapsed'
  { 'w-20': isCollapsed, 'w-64': !isCollapsed },
  
  // Margem para a barra do topo
  'mt-16' 
]">

        <!-- Navigation -->
        <div class="flex-1 overflow-hidden hover:overflow-y-auto">
          <nav class="p-4 space-y-2">
            <a v-for="item in navItems" :key="item.name" :href="item.href" :class="[
              'flex items-center rounded-lg transition-all duration-200',
              'hover:bg-gray-100 dark:hover:bg-gray-700',
              'p-3 group',
              isCollapsed ? 'justify-center' : 'space-x-3',
              isActive(item.href) ? 'sidebar-active' : ''
            ]" :title="isCollapsed ? item.name : ''">
              <div class="flex flex-col items-center justify-center">
                <!-- Ícone -->
                <component :is="item.icon" class="h-5 w-5 flex-shrink-0 transition-transform duration-200 dark:text-[#ffffff] text-[#63465a]"
                  :class="{ 'group-hover:scale-110': isCollapsed }" :style="{ color: 'var(--clinic-icon-color)' }" />
                
                <!-- Selo BETA abaixo do ícone -->
                <span 
                  v-if="item.beta" 
                  class="text-[10px] font-bold text-orange-600 dark:text-orange-400 mt-1"
                  v-show="isCollapsed"
                  :style="{ color: 'var(--clinic-font-color)' }"
                >
                  BETA
                </span>
              </div>

              <!-- Nome do item (ao expandir) -->
              <div class="overflow-hidden transition-all duration-300 ease-out"
                :class="{ 'w-0 opacity-0': isCollapsed, 'w-40 opacity-100': !isCollapsed }">
                <div class="flex flex-col">
                  <span class="whitespace-nowrap" :style="{ color: 'var(--clinic-font-color)' }">{{ item.name }}</span>
                  <span v-if="item.beta && !isCollapsed" class="text-[10px] text-orange-500 font-semibold" :style="{ color: 'var(--clinic-font-color)' }">BETA</span>
                </div>
              </div>
            </a>
          </nav>
        </div>

        <!-- Sidebar Footer - Horário de Brasília -->
        <div class="flex-none px-4 py-2 border-t border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-center p-2">
            <div class="flex items-center space-x-2">
              <Clock class="h-4 w-4" :style="{ color: 'var(--clinic-icon-color)' }" />
              <div class="text-center">
                <div class="text-xs text-gray-600 dark:text-gray-400 font-medium" :style="{ color: 'var(--clinic-font-color)' }">
                  {{ currentTime }}
                </div>          
              </div>
            </div>
          </div>
        </div>

        <!-- Sidebar Footer - Toggle Button -->
        <div class="flex-none px-4 py-4 border-t border-gray-200 dark:border-gray-700">
          <button 
            @click="toggleSidebar"
            class="w-full flex items-center justify-center p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            :style="isSidebarHovered ? { backgroundColor: 'var(--clinic-btn-bg-hover)', color: 'var(--clinic-font-color)' } : {}"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              class="h-5 w-5 transition-transform duration-300"
              :class="{ 'rotate-180': isCollapsed }" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                stroke-linecap="round" 
                stroke-linejoin="round" 
                stroke-width="2" 
                d="M11 19l-7-7 7-7m8 14l-7-7 7-7" 
              />
            </svg>
            <span 
              class="ml-2 text-sm font-medium overflow-hidden transition-all duration-300 ease-out"
              :class="{ 'w-0 opacity-0': isCollapsed, 'w-auto opacity-100': !isCollapsed }"
              :style="{ color: 'var(--clinic-font-color)' }"
            >
              Recolher Menu
            </span>
          </button>
        </div>
      </aside>
    </div>
    
    <!-- Main Content -->
    <div :class="[
      'flex-1 flex flex-col',
      'transition-all duration-300 ease-in-out bg-slate-200 dark:bg-gray-900 ',
      isCollapsed ? 'md:ml-20' : 'md:ml-64',
      'mt-12', // Add top margin to account for the top bar
      'overflow-x-auto mb-16 md:mb-0', // Evita rolagem lateral global
      'relative z-10', // Garante que o conteúdo principal tenha um z-index menor que a barra superior
      'py-2 md:py-0'
    ]">
      <!-- Main Content Area -->
      <main class="flex-1 
       bg-slate-200 dark:bg-gray-900">
        <!-- Page Title (visible on desktop) -->
        <div class="hidden md:block mb-4">
          <h2 class="text-xl font-semibold text-gray-800 dark:text-white">
            {{ pageTitle }}
          </h2>
        </div>
        
        <slot name="content">
          <div class="text-center text-gray-500 dark:text-gray-400">
            Nada para mostrar
          </div>
        </slot>
      </main>
    </div>
    
    <div v-if="isAuthenticated">
      <!-- mobile bottom navigation -->
      <nav
        class="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 md:hidden z-40 overflow-hidden w-full">
        <div ref="navContainer"
          class="relative pb-2 flex w-full max-w-[100vw] overflow-x-auto scrollbar-hidden bg-white/90 dark:bg-gray-800/90 backdrop-blur-md shadow-[0_-2px_10px_rgba(0,0,0,0.05)] whitespace-nowrap">
          <!-- links de navegação -->
          <a v-for="item in navItems.slice(0, 5)" :key="item.name" :href="item.href" :class="[
            'flex flex-col items-center justify-center flex-grow px-4 py-3 transition-all duration-200',
            'active:scale-95'
          ]" :style="{
            backgroundColor: isActive(item.href) ? 'var(--clinic-btn-bg-hover)' : '',
            color: isActive(item.href) ? 'var(--clinic-font-color)' : 'var(--clinic-font-color)'
          }" :title="item.name">
            <div class="relative">
              <component :is="item.icon" class="h-6 w-6 mb-1 transition-transform"
                :class="isActive(item.href) ? 'scale-110' : ''" :style="{ color: 'var(--clinic-icon-color)' }" />
            </div>
            <span class="text-xs font-medium tracking-tight" :style="{ color: 'var(--clinic-font-color)' }">
              {{ item.name }}
            </span>
            <span 
              v-if="item.beta" 
              class="text-[10px] font-bold text-orange-600 dark:text-orange-400"
              :style="{ color: 'var(--clinic-font-color)' }"
            >
              BETA
            </span>
          </a>

          <!-- botão Mais -->
          <button @click="toggleMobileUserMenu" data-user-menu-mobile
            class="flex flex-col items-center justify-center flex-grow px-4 py-3 transition-all duration-200 relative group active:scale-95"
            title="Mais" data-user-menu-mobile-button>
            <div class="relative">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" :class="[
                'stroke-current transition-colors duration-200'
              ]" :style="{ color: 'var(--clinic-icon-color)' }" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="4" x2="20" y1="12" y2="12" />
                <line x1="4" x2="20" y1="6" y2="6" />
                <line x1="4" x2="20" y1="18" y2="18" />
              </svg>
            </div>
            <span
              class="text-xs font-medium tracking-tight"
              :style="{ color: 'var(--clinic-font-color)' }"
            >
              Mais
            </span>
          </button>
        </div>
      </nav>
    </div>
    
    <!-- User Menu Dropdown -->
    <div class="relative" ref="userMenu">
      <transition name="slide-from-avatar">
        <div v-if="isUserMenuOpen"
          class="fixed top-16 right-4 py-4 px-2 w-72 bg-white dark:bg-gray-800 text-gray-800 dark:text-white rounded-2xl shadow-2xl z-[9999] animate-fade-in-up ring-2 ring-gray-100 dark:ring-gray-700 border border-gray-100 dark:border-gray-700">
          <div class="relative">
            <div class="space-y-1">
              <button @click="irParaMeuPerfil"
                class="block w-full text-left px-4 py-3 text-sm font-semibold clinic-text rounded-xl transition-all duration-300 ease-in-out flex items-center justify-between"
                :style="{ color: 'var(--clinic-font-color)' }"
                @mouseenter="$event.target.style.backgroundColor = 'var(--clinic-btn-bg-hover)'"
                @mouseleave="$event.target.style.backgroundColor = ''">
                <span class="flex items-center">
                  <User class="inline-block mr-3 h-5 w-5 clinic-icon" />
                  Meu Perfil
                </span>
              </button>
              <button @click="irParaTermosDeUso"
                class="block w-full text-left px-4 py-3 text-sm font-semibold clinic-text rounded-xl transition-all duration-300 ease-in-out flex items-center justify-between"
                :style="{ color: 'var(--clinic-font-color)' }"
                @mouseenter="$event.target.style.backgroundColor = 'var(--clinic-btn-bg-hover)'"
                @mouseleave="$event.target.style.backgroundColor = ''">
                <span class="flex items-center">
                  <FileText class="inline-block mr-3 h-5 w-5 clinic-icon" />
                  Termos de Uso
                </span>
              </button>
              <button @click="irParaPoliticaPrivacidade"
                class="block w-full text-left px-4 py-3 text-sm font-semibold clinic-text rounded-xl transition-all duration-300 ease-in-out flex items-center justify-between"
                :style="{ color: 'var(--clinic-font-color)' }"
                @mouseenter="$event.target.style.backgroundColor = 'var(--clinic-btn-bg-hover)'"
                @mouseleave="$event.target.style.backgroundColor = ''">
                <span class="flex items-center">
                  <Shield class="inline-block mr-3 h-5 w-5 clinic-icon" />
                  Política de Privacidade
                </span>
              </button>
              <button @click="toggleDarkMode"
                class="block w-full text-left px-4 py-3 text-sm font-semibold clinic-text rounded-xl transition-all duration-300 ease-in-out flex items-center justify-between"
                :style="{ color: 'var(--clinic-font-color)' }"
                @mouseenter="$event.target.style.backgroundColor = 'var(--clinic-btn-bg-hover)'"
                @mouseleave="$event.target.style.backgroundColor = ''">
                <span class="flex items-center">
                  <component :is="isDark ? SunIcon : MoonIcon"
                    class="inline-block mr-3 h-5 w-5 clinic-icon" />
                  {{ isDark ? "Modo Claro" : "Modo Escuro" }}
                </span>
              </button>

             <!-- <button @click="toggleNotifications"
                class="block w-full text-left px-4 py-3 text-sm font-semibold clinic-text hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-300 ease-in-out flex items-center justify-between"
                :disabled="!areNotificationsEnabled">
                <span class="flex items-center">
                  <Bell class="inline-block mr-3 h-5 w-5 clinic-icon" />
                  {{ areNotificationsEnabled ? "Desabilitar Notificações" : "Habilitar Notificações" }}
                </span>
              </button>-->

              <button @click="openPasswordChangeModal"
                class="block w-full text-left px-4 py-3 text-sm font-semibold clinic-text rounded-xl transition-all duration-300 ease-in-out flex items-center justify-between"
                :style="{ color: 'var(--clinic-font-color)' }"
                @mouseenter="$event.target.style.backgroundColor = 'var(--clinic-btn-bg-hover)'"
                @mouseleave="$event.target.style.backgroundColor = ''">
                <span class="flex items-center">
                  <Lock class="inline-block mr-3 h-5 w-5 clinic-icon" />
                  Trocar Senha
                </span>
              </button>

              <button @click="handleLogout"
                class="block w-full text-left px-4 py-3 text-sm font-semibold text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-800 rounded-xl transition-all duration-300 ease-in-out flex items-center justify-between">
                <span class="flex items-center">
                  <LogOut class="inline-block mr-3 h-5 w-5 text-red-600 dark:text-red-400" />
                  Logout
                </span>
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
    
    <!-- Mobile User Menu -->
    <div class="relative" ref="mobileUserMenu">
      <transition name="slide-from-avatar">
        <div v-if="isMobileUserMenuOpen"
          class="fixed bottom-16 left-4 right-4 py-4 px-6 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl z-[9999] ring-2 ring-gray-100 dark:ring-gray-700 border border-gray-100 dark:border-gray-700">
          <div class="flex flex-col space-y-4">
            <!-- Mobile menu items for additional navigation items -->
            <a v-for="item in navItems.slice(5)" :key="item.name" :href="item.href"
              class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 ease-in-out"
              :style="{
                backgroundColor: isActive(item.href) ? 'var(--clinic-btn-bg-hover)' : 'var(--clinic-btn-bg)',
                color: 'var(--clinic-font-color)'
              }"
              @mouseenter="$event.target.style.backgroundColor = 'var(--clinic-btn-bg-hover)'"
              @mouseleave="$event.target.style.backgroundColor = isActive(item.href) ? 'var(--clinic-btn-bg-hover)' : 'var(--clinic-btn-bg)'">
              <span class="flex items-center">
                <component :is="item.icon" class="h-5 w-5 mr-3" :style="{ color: 'var(--clinic-icon-color)' }" />
                {{ item.name }}
              </span>
              <span v-if="item.beta" class="text-[10px] font-bold text-orange-600 dark:text-orange-400" :style="{ color: 'var(--clinic-font-color)' }">
                BETA
              </span>
            </a>
            
            <button @click="irParaMeuPerfil"
              class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold clinic-text bg-white dark:bg-gray-800 rounded-xl transition-all duration-300 ease-in-out"
              :style="{ color: 'var(--clinic-font-color)' }"
              @mouseenter="$event.target.style.backgroundColor = 'var(--clinic-btn-bg-hover)'"
              @mouseleave="$event.target.style.backgroundColor = ''">
              <span class="flex items-center">
                <User class="h-5 w-5 mr-3 clinic-icon" />
                Meu Perfil
              </span>
            </button>
            <button @click="irParaTermosDeUso"
              class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold clinic-text bg-white dark:bg-gray-800 rounded-xl transition-all duration-300 ease-in-out"
              :style="{ color: 'var(--clinic-font-color)' }"
              @mouseenter="$event.target.style.backgroundColor = 'var(--clinic-btn-bg-hover)'"
              @mouseleave="$event.target.style.backgroundColor = ''">
              <span class="flex items-center">
                <FileText class="h-5 w-5 mr-3 clinic-icon" />
                Termos de Uso
              </span>
            </button>
            <button @click="irParaPoliticaPrivacidade"
              class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold clinic-text bg-white dark:bg-gray-800 rounded-xl transition-all duration-300 ease-in-out"
              :style="{ color: 'var(--clinic-font-color)' }"
              @mouseenter="$event.target.style.backgroundColor = 'var(--clinic-btn-bg-hover)'"
              @mouseleave="$event.target.style.backgroundColor = ''">
              <span class="flex items-center">
                <Shield class="h-5 w-5 mr-3 clinic-icon" />
                Política de Privacidade
              </span>
            </button>
            <button @click="toggleDarkMode"
              class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold clinic-text bg-white dark:bg-gray-800 rounded-xl transition-all duration-300 ease-in-out"
              :style="{ color: 'var(--clinic-font-color)' }"
              @mouseenter="$event.target.style.backgroundColor = 'var(--clinic-btn-bg-hover)'"
              @mouseleave="$event.target.style.backgroundColor = ''">
              <span class="flex items-center">
                <component :is="isDark ? SunIcon : MoonIcon" class="h-5 w-5 mr-3 clinic-icon" />
                {{ isDark ? "Modo Claro" : "Modo Escuro" }}
              </span>
            </button>

            <button @click="toggleNotifications"
              class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold clinic-text bg-white dark:bg-gray-800 rounded-xl transition-all duration-300 ease-in-out"
              :style="{ color: 'var(--clinic-font-color)' }"
              @mouseenter="$event.target.style.backgroundColor = 'var(--clinic-btn-bg-hover)'"
              @mouseleave="$event.target.style.backgroundColor = ''"
              :disabled="!areNotificationsEnabled">
              <span class="flex items-center">
                <Bell class="h-5 w-5 mr-3 clinic-icon" />
                {{ areNotificationsEnabled ? "Desabilitar Notificações" : "Habilitar Notificações" }}
              </span>
            </button>

            <button @click="openPasswordChangeModal"
              class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold clinic-text bg-white dark:bg-gray-800 rounded-xl transition-all duration-300 ease-in-out"
              :style="{ color: 'var(--clinic-font-color)' }"
              @mouseenter="$event.target.style.backgroundColor = 'var(--clinic-btn-bg-hover)'"
              @mouseleave="$event.target.style.backgroundColor = ''">
              <span class="flex items-center">
                <Lock class="h-5 w-5 mr-3 clinic-icon" />
                Trocar Senha
              </span>
            </button>

            <button @click="handleLogout"
              class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 rounded-xl transition-all duration-300 ease-in-out">
              <span class="flex items-center">
                <LogOut class="h-5 w-5 mr-3 text-red-600 dark:text-red-400" />
                Logout
              </span>
            </button>
          </div>
        </div>
      </transition>
    </div>
    
    <!-- Password Change Modal -->
    <transition name="fade">
      <div v-if="isPasswordChangeModalOpen"
        class="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 mx-5">
          <h2 class="text-xl font-bold mb-4 text-gray-800 dark:text-gray-200">Trocar Senha</h2>

          <!-- Mensagem de Sucesso -->
          <div v-if="successMessage" class="mb-4 p-4 text-green-700 bg-green-100 border-l-4 border-green-500 rounded">
            {{ successMessage }}
          </div>

          <!-- Mensagem de Erro -->
          <div v-if="errorMessage" class="mb-4 p-4 text-red-700 bg-red-100 border-l-4 border-red-500 rounded">
            {{ errorMessage }}
          </div>

          <form @submit.prevent="handleChangePassword">
            <div class="mb-4">
              <label for="current-password" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Senha
                Atual</label>
              <input v-model="currentPassword" type="password" id="current-password" required
                class="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 focus:ring-pink-500 focus:border-pink-500" />
            </div>
            <div class="mb-4">
              <label for="new-password" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Nova
                Senha</label>
              <input v-model="newPassword" type="password" id="new-password" required
                class="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 focus:ring-pink-500 focus:border-pink-500" />
            </div>
            <div class="mb-4">
              <label for="confirm-password" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Confirme
                a Nova Senha</label>
              <input v-model="confirmPassword" type="password" id="confirm-password" required
                class="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 focus:ring-pink-500 focus:border-pink-500" />
            </div>
            <div class="flex justify-end space-x-4">
              <button @click="closePasswordChangeModal" type="button"
                class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-700">
                Cancelar
              </button>
              <button type="submit"
                class="px-4 py-2 text-sm font-medium rounded-md"
                :style="{ backgroundColor: 'var(--clinic-btn-bg)', color: 'var(--clinic-font-color)' }">
                Salvar
              </button>
            </div>
          </form>
        </div>
      </div>
    </transition>

    <!-- Terms of Service Modal -->
    <transition name="fade">
      <div v-if="isTermsModalOpen"
        class="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] p-6 mx-5 overflow-y-auto">
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-200">Termos de Uso - InovaMed</h2>
            <button @click="closeTermsModal" class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-2xl">
              ✕
            </button>
          </div>
          
          <div class="prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
            <div class="mb-6">
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Última atualização: 12/09/2025</p>
              
              <h3 class="text-lg font-semibold mb-3">1. Aceitação dos Termos</h3>
              <p class="mb-4">Ao acessar e usar o sistema InovaMed, você concorda em cumprir e estar vinculado a estes Termos de Uso. Se você não concordar com qualquer parte destes termos, não deve usar nosso sistema.</p>
              
              <h3 class="text-lg font-semibold mb-3">2. Descrição do Serviço</h3>
              <p class="mb-4">O InovaMed é um sistema de gestão médica que oferece funcionalidades para gerenciamento de pacientes, agendamentos, relatórios médicos, e outras ferramentas relacionadas à administração de clínicas e consultórios médicos.</p>
              
              <h3 class="text-lg font-semibold mb-3">3. Elegibilidade</h3>
              <p class="mb-4">Você deve ter pelo menos 18 anos de idade para usar este sistema. Ao usar o InovaMed, você declara e garante que tem pelo menos 18 anos de idade.</p>
              
              <h3 class="text-lg font-semibold mb-3">4. Responsabilidades do Usuário</h3>
              <ul class="list-disc pl-6 mb-4">
                <li>Manter a confidencialidade de suas credenciais de acesso</li>
                <li>Usar o sistema apenas para fins legítimos e autorizados</li>
                <li>Não compartilhar informações confidenciais de pacientes</li>
                <li>Cumprir todas as leis e regulamentações aplicáveis</li>
                <li>Reportar qualquer uso não autorizado de sua conta</li>
              </ul>
              
              <h3 class="text-lg font-semibold mb-3">5. Proteção de Dados</h3>
              <p class="mb-4">Levamos a sério a proteção de dados pessoais e seguimos rigorosamente a Lei Geral de Proteção de Dados (LGPD). Todos os dados são tratados com confidencialidade e segurança adequadas.</p>
              
              <h3 class="text-lg font-semibold mb-3">6. Propriedade Intelectual</h3>
              <p class="mb-4">Todo o conteúdo, funcionalidades e tecnologia do InovaMed são propriedade exclusiva da nossa empresa e são protegidos por leis de direitos autorais, marcas registradas e outras leis de propriedade intelectual.</p>
              
              <h3 class="text-lg font-semibold mb-3">7. Disponibilidade do Serviço</h3>
              <p class="mb-4">Embora nos esforcemos para manter o sistema disponível 24/7, não garantimos que o serviço será ininterrupto ou livre de erros. Podemos realizar manutenções programadas que podem afetar temporariamente a disponibilidade.</p>
              
              <h3 class="text-lg font-semibold mb-3">8. Limitação de Responsabilidade</h3>
              <p class="mb-4">Em nenhuma circunstância seremos responsáveis por danos indiretos, incidentais, especiais, consequenciais ou punitivos resultantes do uso ou incapacidade de usar o sistema.</p>
              
              <h3 class="text-lg font-semibold mb-3">9. Modificações dos Termos</h3>
              <p class="mb-4">Reservamo-nos o direito de modificar estes termos a qualquer momento. As alterações entrarão em vigor imediatamente após a publicação no sistema.</p>
              
              <h3 class="text-lg font-semibold mb-3">10. Rescisão</h3>
              <p class="mb-4">Podemos encerrar ou suspender sua conta e acesso ao sistema imediatamente, sem aviso prévio, por qualquer motivo, incluindo violação destes Termos de Uso.</p>
              
              <h3 class="text-lg font-semibold mb-3">11. Lei Aplicável</h3>
              <p class="mb-4">Estes Termos de Uso são regidos pelas leis do Brasil. Qualquer disputa será resolvida nos tribunais competentes do Brasil.</p>
              
              <h3 class="text-lg font-semibold mb-3">12. Contato</h3>
              <p class="mb-4">Se você tiver dúvidas sobre estes Termos de Uso, entre em contato conosco através dos canais de suporte disponíveis no sistema.</p>
            </div>
          </div>
          
          <div class="flex justify-end space-x-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
            <button @click="closeTermsModal"
              class="px-6 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors">
              Fechar
            </button>
            <button @click="printTerms"
              class="px-6 py-2 text-sm font-medium rounded-md transition-colors"
              :style="{ backgroundColor: 'var(--clinic-btn-bg)', color: 'var(--clinic-font-color)' }">
              Imprimir
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Privacy Policy Modal -->
    <transition name="fade">
      <div v-if="isPrivacyModalOpen"
        class="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] p-6 mx-5 overflow-y-auto">
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-200">Política de Privacidade - InovaMed</h2>
            <button @click="closePrivacyModal" class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-2xl">
              ✕
            </button>
          </div>
          
          <div class="prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
            <div class="mb-6">
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Última atualização: 12/09/2025</p>
              
              <h3 class="text-lg font-semibold mb-3">1. Introdução</h3>
              <p class="mb-4">Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos suas informações pessoais quando você utiliza o sistema InovaMed, em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).</p>
              
              <h3 class="text-lg font-semibold mb-3">2. Dados Coletados</h3>
              <p class="mb-2">Coletamos os seguintes tipos de informações:</p>
              <ul class="list-disc pl-6 mb-4">
                <li><strong>Dados de Identificação:</strong> Nome, CPF, CNS, RG, data de nascimento</li>
                <li><strong>Dados de Contato:</strong> E-mail, telefone, endereço</li>
                <li><strong>Dados Profissionais:</strong> CRM, especialidade, dados da clínica</li>
                <li><strong>Dados de Acesso:</strong> Login, senha, logs de acesso</li>
                <li><strong>Dados de Pacientes:</strong> Informações médicas conforme autorização</li>
              </ul>
              
              <h3 class="text-lg font-semibold mb-3">3. Finalidade do Tratamento</h3>
              <p class="mb-2">Utilizamos seus dados para:</p>
              <ul class="list-disc pl-6 mb-4">
                <li>Fornecer e manter o funcionamento do sistema</li>
                <li>Autenticar usuários e controlar acesso</li>
                <li>Processar e armazenar informações médicas</li>
                <li>Gerar relatórios e estatísticas</li>
                <li>Faturamento dos exames realizados</li>
                <li>Cumprir obrigações legais e regulatórias</li>
                <li>Melhorar nossos serviços</li>
              </ul>
              
              <h3 class="text-lg font-semibold mb-3">4. Base Legal</h3>
              <p class="mb-4">O tratamento de dados pessoais é baseado no consentimento do titular, execução de contrato, cumprimento de obrigação legal e legítimo interesse, conforme previsto na LGPD.</p>
              
              <h3 class="text-lg font-semibold mb-3">5. Compartilhamento de Dados</h3>
              <p class="mb-4">Não compartilhamos seus dados pessoais com terceiros, exceto quando necessário para cumprir obrigações legais, ordem judicial ou com seu consentimento expresso.</p>
              
              <h3 class="text-lg font-semibold mb-3">6. Armazenamento e Segurança</h3>
              <p class="mb-4">Implementamos medidas técnicas e organizacionais adequadas para proteger seus dados contra acesso não autorizado, alteração, divulgação ou destruição. Os dados são armazenados em servidores seguros com criptografia.</p>
              
              <h3 class="text-lg font-semibold mb-3">7. Retenção de Dados</h3>
              <p class="mb-4">Mantemos seus dados pelo tempo necessário para cumprir as finalidades descritas nesta política, respeitando os prazos legais de retenção de documentos médicos.</p>
              
              <h3 class="text-lg font-semibold mb-3">8. Seus Direitos</h3>
              <p class="mb-2">Conforme a LGPD, você tem direito a:</p>
              <ul class="list-disc pl-6 mb-4">
                <li>Confirmação da existência de tratamento</li>
                <li>Acesso aos dados</li>
                <li>Correção de dados incompletos, inexatos ou desatualizados</li>
                <li>Anonimização, bloqueio ou eliminação de dados desnecessários</li>
                <li>Portabilidade dos dados</li>
                <li>Eliminação dos dados tratados com consentimento</li>
                <li>Revogação do consentimento</li>
              </ul>
              
              <h3 class="text-lg font-semibold mb-3">9. Cookies e Tecnologias Similares</h3>
              <p class="mb-4">Utilizamos cookies e tecnologias similares para melhorar sua experiência, manter sessões ativas e coletar informações sobre o uso do sistema.</p>
              
              <h3 class="text-lg font-semibold mb-3">10. Alterações na Política</h3>
              <p class="mb-4">Esta política pode ser atualizada periodicamente. Notificaremos sobre mudanças significativas através do sistema ou por e-mail.</p>
              
              <h3 class="text-lg font-semibold mb-3">11. Contato</h3>
              <p class="mb-4">Para exercer seus direitos ou esclarecer dúvidas sobre esta política, entre em contato através dos canais de suporte disponíveis no sistema.</p>
              
              <h3 class="text-lg font-semibold mb-3">12. Autoridade Nacional</h3>
              <p class="mb-4">Você também pode entrar em contato com a Autoridade Nacional de Proteção de Dados (ANPD) para questões relacionadas ao tratamento de dados pessoais.</p>
            </div>
          </div>
          
          <div class="flex justify-end space-x-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
            <button @click="closePrivacyModal"
              class="px-6 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors">
              Fechar
            </button>
            <button @click="printPrivacy"
              class="px-6 py-2 text-sm font-medium rounded-md transition-colors"
              :style="{ backgroundColor: 'var(--clinic-btn-bg)', color: 'var(--clinic-font-color)' }">
              Imprimir
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Notificação Pop-up (Desktop) -->
    <transition name="fade">
      <div v-if="isPopupVisible && !isMobile"
        class="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200 p-4 rounded-lg shadow-lg w-80">
        <div class="flex items-start space-x-4">
          <div class="flex-shrink-0">
            <Bell class="h-6 w-6" :style="{ color: 'var(--clinic-icon-color)' }" />
          </div>
          <div class="flex-1">
            <h3 class="text-sm font-semibold">{{ popupMessage.title }}</h3>
            <p class="text-sm">{{ popupMessage.body }}</p>
          </div>
          <button @click="closePopup" class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            ✕
          </button>
        </div>
      </div>
    </transition>

    <!-- Notificação Estilo Push (Mobile) -->
    <transition name="slide">
      <div v-if="isPopupVisible && isMobile"
        class="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 bg-gray-900 text-white dark:bg-gray-200 dark:text-gray-900 p-3 rounded-lg shadow-lg w-72 flex items-center space-x-3">
        <Bell class="h-6 w-6 text-yellow-400" :style="{ color: 'var(--clinic-icon-color)' }" />
        <div class="flex-1">
          <h3 class="text-sm font-semibold">{{ popupMessage.title }}</h3>
          <p class="text-xs">{{ popupMessage.body }}</p>
        </div>
        <button @click="closePopup" class="text-gray-400 hover:text-gray-200 dark:hover:text-gray-800">
          ✕
        </button>
      </div>
    </transition>
  </div>
  <TermoNotification 
  :show="isTermoNotificationVisible" 
  @confirmar="aceitarNotificacoes" 
  @cancelar="recusarNotificacoes" 
/>

</template>

<script setup>
import { useRouter } from 'vue-router';
import { ref, onMounted, computed, onBeforeUnmount, defineProps, watch, provide } from 'vue';
import TermoNotification from '@/views/notifications/TermoNotification.vue'
import { usePerfilStore } from '@/stores/settings/perfilStore'
import { logout } from '@/services/authService';
// Firebase será importado dinamicamente apenas em HTTPS
import {
  LogOut,
  Lock,
  Bell,
  SunIcon,
  CalendarPlus2,
  MoonIcon,
  Settings,
  House,
  ClipboardMinus,
  User,
  UserPlus,
  Stethoscope,
  ScrollText,
  Send,
  Edit,
  PhoneCall,
  Globe,
  DollarSign,
  Package,
  Clock,
  FileText,
  Shield

} from 'lucide-vue-next';

import apiClient from '@/services/authService'
import { useUserStore } from '@/stores/userStore';
import { usePermissionStore } from '@/stores/permissionStore';
//import NotificationsPage from '../notifications/NotificationPage.vue';
import { useClinicStore } from '@/stores/settings/clinicStore';

const userStore = useUserStore();
const permissionStore = usePermissionStore();
const isAuthenticated = computed(() => userStore.autenticado);

const props = defineProps({
  appName: {
    type: String,
    default: 'InovaMed'
  },
  pageTitle: {
    type: String,
    default: ''
  },
  userAvatar: {
    type: String,
    default: null
  },
  customNavItems: {
    type: Array,
    default: () => []
  },
  leftLogo: {
    type: String,
    default: null
  },
  centerLogo: {
    type: String,
    default: null
  }
});

const termoAceito = ref(false);
const route = useRouter();
const isTermoNotificationVisible = ref(false);
const isDark = ref(false);
const isUserMenuOpen = ref(false);
const isMobileUserMenuOpen = ref(false);
const isNotificationMenuOpen = ref(false);
const isSidebarOpen = ref(false);
const isCollapsed = ref(true);
const notifications = ref([]);
const isPasswordChangeModalOpen = ref(false);
const currentPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const errorMessage = ref('');
const successMessage = ref('');
const isTermsModalOpen = ref(false);
const isPrivacyModalOpen = ref(false);
const clinicStore = useClinicStore();

const verificarTermoAceite = async () => {
  try {
    const response = await apiClient.get('/api/v1/audit/accept/notify/status/');
    termoAceito.value = response.data.termo_aceite;
  } catch (error) {
    termoAceito.value = false;
  }
};

const aceitarNotificacoes = async () => {
  isTermoNotificationVisible.value = false;

  try {
    await apiClient.post('/api/v1/audit/accept/notify/', {
      detalhes: `Usuário aceitou o termo de notificações em ${new Date().toLocaleString('pt-BR')}`,
    });

    termoAceito.value = true;

    await subscribeUser();
    console.log('Termo aceito e registrado.');
  } catch (error) {
    console.error('Erro ao registrar aceite do termo:', error);
  }
};


const recusarNotificacoes = () => {
  isTermoNotificationVisible.value = false;
};

// Função para navegar para a página de histórico completo de notificações
//const navigateToNotificationsHistory = () => {
 // route.push({ name: 'NotificationsHistory' });
//};

const irParaMeuPerfil = () => {
  route.push({ name: 'MeuPerfil' }); 
};

const irParaTermosDeUso = () => {
  isTermsModalOpen.value = true;
};

const irParaPoliticaPrivacidade = () => {
  isPrivacyModalOpen.value = true;
};

const closeTermsModal = () => {
  isTermsModalOpen.value = false;
};

const closePrivacyModal = () => {
  isPrivacyModalOpen.value = false;
};

const printTerms = () => {
  const printWindow = window.open('', '_blank');
  const termsContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Termos de Uso - InovaMed</title>
      <meta charset="UTF-8">
      <style>
        body {
          font-family: Arial, sans-serif;
          line-height: 1.6;
          margin: 40px;
          color: #333;
        }
        h1 {
          color: #2c3e50;
          border-bottom: 2px solid #3498db;
          padding-bottom: 10px;
        }
        h3 {
          color: #34495e;
          margin-top: 25px;
          margin-bottom: 15px;
        }
        p {
          margin-bottom: 15px;
          text-align: justify;
        }
        ul {
          margin-bottom: 15px;
        }
        li {
          margin-bottom: 8px;
        }
        .update-date {
          font-size: 14px;
          color: #666;
          font-style: italic;
          margin-bottom: 30px;
        }
        @media print {
          body { margin: 20px; }
          h1 { color: #000 !important; }
          h3 { color: #000 !important; }
        }
      </style>
    </head>
    <body>
      <h1>Termos de Uso - InovaMed</h1>
      <p class="update-date">Última atualização: 12/09/2025</p>
      
      <h3>1. Aceitação dos Termos</h3>
      <p>Ao acessar e usar o sistema InovaMed, você concorda em cumprir e estar vinculado a estes Termos de Uso. Se você não concordar com qualquer parte destes termos, não deve usar nosso sistema.</p>
      
      <h3>2. Descrição do Serviço</h3>
      <p>O InovaMed é um sistema de gestão médica que oferece funcionalidades para gerenciamento de pacientes, agendamentos, relatórios médicos, e outras ferramentas relacionadas à administração de clínicas e consultórios médicos.</p>
      
      <h3>3. Elegibilidade</h3>
      <p>Você deve ter pelo menos 18 anos de idade para usar este sistema. Ao usar o InovaMed, você declara e garante que tem pelo menos 18 anos de idade.</p>
      
      <h3>4. Responsabilidades do Usuário</h3>
      <ul>
        <li>Manter a confidencialidade de suas credenciais de acesso</li>
        <li>Usar o sistema apenas para fins legítimos e autorizados</li>
        <li>Não compartilhar informações confidenciais de pacientes</li>
        <li>Cumprir todas as leis e regulamentações aplicáveis</li>
        <li>Reportar qualquer uso não autorizado de sua conta</li>
      </ul>
      
      <h3>5. Proteção de Dados</h3>
      <p>Levamos a sério a proteção de dados pessoais e seguimos rigorosamente a Lei Geral de Proteção de Dados (LGPD). Todos os dados são tratados com confidencialidade e segurança adequadas.</p>
      
      <h3>6. Propriedade Intelectual</h3>
      <p>Todo o conteúdo, funcionalidades e tecnologia do InovaMed são propriedade exclusiva da nossa empresa e são protegidos por leis de direitos autorais, marcas registradas e outras leis de propriedade intelectual.</p>
      
      <h3>7. Disponibilidade do Serviço</h3>
      <p>Embora nos esforcemos para manter o sistema disponível 24/7, não garantimos que o serviço será ininterrupto ou livre de erros. Podemos realizar manutenções programadas que podem afetar temporariamente a disponibilidade.</p>
      
      <h3>8. Limitação de Responsabilidade</h3>
      <p>Em nenhuma circunstância seremos responsáveis por danos indiretos, incidentais, especiais, consequenciais ou punitivos resultantes do uso ou incapacidade de usar o sistema.</p>
      
      <h3>9. Modificações dos Termos</h3>
      <p>Reservamo-nos o direito de modificar estes termos a qualquer momento. As alterações entrarão em vigor imediatamente após a publicação no sistema.</p>
      
      <h3>10. Rescisão</h3>
      <p>Podemos encerrar ou suspender sua conta e acesso ao sistema imediatamente, sem aviso prévio, por qualquer motivo, incluindo violação destes Termos de Uso.</p>
      
      <h3>11. Lei Aplicável</h3>
      <p>Estes Termos de Uso são regidos pelas leis do Brasil. Qualquer disputa será resolvida nos tribunais competentes do Brasil.</p>
      
      <h3>12. Contato</h3>
      <p>Se você tiver dúvidas sobre estes Termos de Uso, entre em contato conosco através dos canais de suporte disponíveis no sistema.</p>
    </body>
    </html>
  `;
  
  printWindow.document.write(termsContent);
  printWindow.document.close();
  printWindow.onload = function() { 
    printWindow.print(); 
    printWindow.close();
  };
};

const printPrivacy = () => {
  const printWindow = window.open('', '_blank');
  const privacyContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Política de Privacidade - InovaMed</title>
      <meta charset="UTF-8">
      <style>
        body {
          font-family: Arial, sans-serif;
          line-height: 1.6;
          margin: 40px;
          color: #333;
        }
        h1 {
          color: #2c3e50;
          border-bottom: 2px solid #3498db;
          padding-bottom: 10px;
        }
        h3 {
          color: #34495e;
          margin-top: 25px;
          margin-bottom: 15px;
        }
        p {
          margin-bottom: 15px;
          text-align: justify;
        }
        ul {
          margin-bottom: 15px;
        }
        li {
          margin-bottom: 8px;
        }
        .update-date {
          font-size: 14px;
          color: #666;
          font-style: italic;
          margin-bottom: 30px;
        }
        strong {
          font-weight: bold;
        }
        @media print {
          body { margin: 20px; }
          h1 { color: #000 !important; }
          h3 { color: #000 !important; }
        }
      </style>
    </head>
    <body>
      <h1>Política de Privacidade - InovaMed</h1>
      <p class="update-date">Última atualização: 12/09/2025</p>
      
      <h3>1. Introdução</h3>
      <p>Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos suas informações pessoais quando você utiliza o sistema InovaMed, em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).</p>
      
      <h3>2. Dados Coletados</h3>
      <p>Coletamos os seguintes tipos de informações:</p>
      <ul>
        <li><strong>Dados de Identificação:</strong> Nome, CPF, CNS, RG, data de nascimento</li>
        <li><strong>Dados de Contato:</strong> E-mail, telefone, endereço</li>
        <li><strong>Dados Profissionais:</strong> CRM, especialidade, dados da clínica</li>
        <li><strong>Dados de Acesso:</strong> Login, senha, logs de acesso</li>
        <li><strong>Dados de Pacientes:</strong> Informações médicas conforme autorização</li>
      </ul>
      
      <h3>3. Finalidade do Tratamento</h3>
      <p>Utilizamos seus dados para:</p>
      <ul>
        <li>Fornecer e manter o funcionamento do sistema</li>
        <li>Autenticar usuários e controlar acesso</li>
        <li>Processar e armazenar informações médicas</li>
        <li>Gerar relatórios e estatísticas</li>
        <li>Faturamento dos exames realizados</li>
        <li>Cumprir obrigações legais e regulatórias</li>
        <li>Melhorar nossos serviços</li>
      </ul>
      
      <h3>4. Base Legal</h3>
      <p>O tratamento de dados pessoais é baseado no consentimento do titular, execução de contrato, cumprimento de obrigação legal e legítimo interesse, conforme previsto na LGPD.</p>
      
      <h3>5. Compartilhamento de Dados</h3>
      <p>Não compartilhamos seus dados pessoais com terceiros, exceto quando necessário para cumprir obrigações legais, ordem judicial ou com seu consentimento expresso.</p>
      
      <h3>6. Armazenamento e Segurança</h3>
      <p>Implementamos medidas técnicas e organizacionais adequadas para proteger seus dados contra acesso não autorizado, alteração, divulgação ou destruição. Os dados são armazenados em servidores seguros com criptografia.</p>
      
      <h3>7. Retenção de Dados</h3>
      <p>Mantemos seus dados pelo tempo necessário para cumprir as finalidades descritas nesta política, respeitando os prazos legais de retenção de documentos médicos.</p>
      
      <h3>8. Seus Direitos</h3>
      <p>Conforme a LGPD, você tem direito a:</p>
      <ul>
        <li>Confirmação da existência de tratamento</li>
        <li>Acesso aos dados</li>
        <li>Correção de dados incompletos, inexatos ou desatualizados</li>
        <li>Anonimização, bloqueio ou eliminação de dados desnecessários</li>
        <li>Portabilidade dos dados</li>
        <li>Eliminação dos dados tratados com consentimento</li>
        <li>Revogação do consentimento</li>
      </ul>
      
      <h3>9. Cookies e Tecnologias Similares</h3>
      <p>Utilizamos cookies e tecnologias similares para melhorar sua experiência, manter sessões ativas e coletar informações sobre o uso do sistema.</p>
      
      <h3>10. Alterações na Política</h3>
      <p>Esta política pode ser atualizada periodicamente. Notificaremos sobre mudanças significativas através do sistema ou por e-mail.</p>
      
      <h3>11. Contato</h3>
      <p>Para exercer seus direitos ou esclarecer dúvidas sobre esta política, entre em contato através dos canais de suporte disponíveis no sistema.</p>
      
      <h3>12. Autoridade Nacional</h3>
      <p>Você também pode entrar em contato com a Autoridade Nacional de Proteção de Dados (ANPD) para questões relacionadas ao tratamento de dados pessoais.</p>
    </body>
    </html>
  `;
  
  printWindow.document.write(privacyContent);
  printWindow.document.close();
  printWindow.onload = function() { 
    printWindow.print(); 
    printWindow.close();
  };
};

const getUserName = computed(() => {
  return `${perfil.firstName || userStore.nome || ''} ${perfil.lastName || ''}`.trim();
});

const openPasswordChangeModal = () => {
  isPasswordChangeModalOpen.value = true;
};

const handleImageError = () => {
  // Ao ocorrer um erro no carregamento da imagem (por exemplo, erro 403),
  // Limpa os dados do perfil e força uma nova consulta.
  const perfilStore = usePerfilStore();
  perfilStore.limparPerfil();
  perfilStore.carregarPerfil();
};

const handleLogoError = () => {
  // Ao ocorrer um erro no carregamento da logo, define a logo padrão
  console.error('Erro ao carregar logo da clínica:', clinicStore.clinic.logo);
  clinicStore.clinic.logo = '/styles/public/inovamed.png';
};

const closePasswordChangeModal = () => {
  isPasswordChangeModalOpen.value = false;
  currentPassword.value = '';
  newPassword.value = '';
  confirmPassword.value = '';
  errorMessage.value = '';
  successMessage.value = '';
};

const handleChangePassword = async () => {
  // Limpa mensagens de erro e sucesso antes de executar
  errorMessage.value = '';
  successMessage.value = '';

  // Verifica se as senhas coincidem
  if (newPassword.value !== confirmPassword.value) {
    errorMessage.value = 'As senhas não coincidem.';
    return;
  }

  // Verifica se os campos estão preenchidos
  if (!currentPassword.value || !newPassword.value || !confirmPassword.value) {
    errorMessage.value = 'Por favor, preencha todos os campos.';
    return;
  }

  try {
    // Realiza a requisição para a API de alteração de senha
    const response = await apiClient.post(
      `/api/v1/auth/change-password/`,
      {
        current_password: currentPassword.value,
        new_password: newPassword.value,
      },
    );

    // Exibe mensagem de sucesso
    successMessage.value = response.data.detail || 'Senha alterada com sucesso.';
    setTimeout(() => {
      closePasswordChangeModal(); // Fecha o modal após a alteração
    }, 2000);
  } catch (error) {
    // Lida com erros retornados pela API
    if (error.response?.status === 400) {
      errorMessage.value = error.response.data.detail || 'Erro ao alterar a senha.';
    } else if (error.response?.status === 401) {
      errorMessage.value = 'Sessão expirada. Por favor, faça login novamente.';
    } else {
      errorMessage.value = 'Erro inesperado ao alterar a senha. Tente novamente mais tarde.';
    }
  }
};

const getUserInitials = computed(() => {
  const first = perfil.firstName?.charAt(0).toUpperCase() || '';
  const last = perfil.lastName?.charAt(0).toUpperCase() || '';
  return `${first}${last}`;
});

// Acesso seguro em computeds ou templates
//const hasNotifications = computed(() => notifications.value?.length > 0);

// Simulação de preenchimento de notificações para teste
onMounted(() => {
  verificarTermoAceite();
  notifications.value = [ 
    { id: 1, text: 'Nova mensagem recebida' },
    { id: 2, text: 'Atualização de sistema disponível' },
  ];
});

const isActive = (href) => {
  return route.currentRoute.value.path === href;
};

// Computando os itens de navegação com base nas permissões do usuário
const navItems = computed(() => {
  return props.customNavItems.length > 0
    ? props.customNavItems
    : [
    {
          name: 'Início',
          icon: House,
          href: '/home',
          current: false,
          requiredPermission: 'can_access_home',
        },
        {
          name: 'Agenda',
          icon: CalendarPlus2,
          href: '/schedule',
          current: false,
          requiredPermission: 'can_access_schedule',
        },
                {
          name: 'Pacientes',
          icon: UserPlus,
          href: '/patients',
          current: false,
          requiredPermission: 'can_access_patients',
        },
        {
          name: 'Atendimento',
          icon: Stethoscope,
          href: '/atendimento',
          current: false,
          requiredPermission: 'can_access_atendimento',
        },
        {
          name: 'Laudos',
          icon: ClipboardMinus,
          href: '/medicalreports',
          current: false,
          requiredPermission: 'can_access_medicalreports',
        },
        {
          name: 'Liberar Exames',
          icon: Globe,
          href: '/exam-release',
          current: false,
          requiredPermission: 'can_access_exam_release',
        },
        {
          name: 'Editar Exame',
          icon: Edit,
          href: '/editexam',
          current: false,
          requiredPermission: 'can_access_edit_exam',
        },
        {
          name: 'Entrega de Exames',
          icon: Send,
          href: '/entrega-exames',
          current: false,
          requiredPermission: 'can_access_exam_delivery',
        },
        {
          name: 'Fila de Reconvocação',
          icon: PhoneCall,
          href: '/recall-queue',
          current: false,
          requiredPermission: 'can_access_recall_queue',
        },
        {
          name: 'Relatórios',
          icon: ScrollText,
          href: '/reports',
          current: false,
          requiredPermission: 'can_access_reports',
        },
        {
          name: 'Faturamento',
          icon: DollarSign,
          href: '/faturamento',
          current: false,
          requiredPermission: 'Faturamento.can_access_faturamento',
        },
        {
          name: 'Estoque',
          icon: Package,
          href: '/estoque',
          current: false,
          requiredPermission: 'Estoque.can_access_estoque',
        },
 
      {
        name: 'Configurações',
        icon: Settings,
        href: '/config',
        current: false,
        requiredPermission: 'can_access_config',
      },
      ].filter(item => {
        // Filtra os itens com base nas permissões do usuário
        if (item.requiredPermission) {
          return permissionStore.hasPermission(item.requiredPermission);
        }
        // Caso contrário, o item é visível para todos
        return true;
      });
});

// Atualiza a variável CSS dinamicamente
onMounted(() => {
  document.documentElement.style.setProperty('--visible-items', navItems.value.length);
});

watch(navItems, (newItems) => {
  document.documentElement.style.setProperty('--visible-items', newItems.length);
});

const areNotificationsEnabled = ref(false);

// Carregar valor do sessionStorage ao montar o componente
onMounted(() => {
  const storedValue = sessionStorage.getItem('notifications_enabled');
  areNotificationsEnabled.value = storedValue === 'true'; // Converte string para booleano
});

// Observa mudanças e atualiza o sessionStorage
watch(areNotificationsEnabled, (newValue) => {
  sessionStorage.setItem('notifications_enabled', newValue.toString());
});

// Função para ativar notificações
async function subscribeUser() {
  try {
    // Verifica se está em HTTPS
    if (window.location.protocol !== 'https:') {
      console.warn('Firebase Messaging só funciona em HTTPS');
      return;
    }

    if (!("serviceWorker" in navigator)) {
      console.error("Service Worker não é suportado pelo navegador.");
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.error("Permissão para notificações negada.");
      return;
    }

    // Importa Firebase dinamicamente apenas em HTTPS
    const { getMessaging, getToken } = await import("firebase/messaging");
    const messaging = getMessaging();

    const token = await getToken(messaging, {
      vapidKey: "BAPGqPkCrJf81vFo_X--nzF-T3QLyDqFYTaXPKap-_Ar856j6zcor8AdSuFIHd7P-0EQXSRroEKs8RKZ2caQ6pU",
    });

    console.log("Token FCM:", token);

    await apiClient.post(`/api/v1/notifications/register`, { token });

    areNotificationsEnabled.value = true; // Atualiza o estado local
    console.log("Notificações ativadas.");
  } catch (error) {
    console.error("Erro ao inscrever o usuário:", error);
  }
}

// Função para desativar notificações
async function disableNotifications() {
  try {
    // Verifica se está em HTTPS
    if (window.location.protocol !== 'https:') {
      console.warn('Firebase Messaging só funciona em HTTPS');
      return;
    }

    // Importa Firebase dinamicamente apenas em HTTPS
    const { getMessaging, getToken } = await import("firebase/messaging");
    const messaging = getMessaging();

    const token = await getToken(messaging, {
      vapidKey: "BAPGqPkCrJf81vFo_X--nzF-T3QLyDqFYTaXPKap-_Ar856j6zcor8AdSuFIHd7P-0EQXSRroEKs8RKZ2caQ6pU",
    });

    console.log("Removendo token:", token);

    await apiClient.post(`/api/v1/notifications/register?disable=true`, { token });

    areNotificationsEnabled.value = false; // Atualiza o estado local
    console.log("Notificações desativadas.");
  } catch (error) {
    console.error("Erro ao desativar notificações:", error);
  }
}

// Função para alternar notificações
const toggleNotifications = async () => {
  if (areNotificationsEnabled.value) {
    await disableNotifications();
  } else {
    if (termoAceito.value) {
      await subscribeUser(); // Já aceitou antes, ativa direto
    } else {
      isTermoNotificationVisible.value = true; // Ainda não aceitou, exibe termo
    }
  }
};



const toggleSidebar = () => {
  if (window.innerWidth >= 768) {
    isCollapsed.value = !isCollapsed.value;
  } else {
    isSidebarOpen.value = !isSidebarOpen.value;
  }
};

const toggleDarkMode = () => {
  isDark.value = !isDark.value;
  localStorage.setItem('darkMode', isDark.value);
  document.documentElement.classList.toggle('dark', isDark.value);
  
  // Aplica imediatamente as cores do clinicStore baseado no novo modo
  clinicStore.applyClinicStyles();
};


provide('isDark', isDark);

onMounted(async () => {
  // Verifica se já existe preferência no localStorage
  const storedDarkMode = localStorage.getItem('darkMode');
  if (storedDarkMode === null) {
    // Primeiro acesso: utiliza a preferência do sistema
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    isDark.value = prefersDark;
    localStorage.setItem('darkMode', prefersDark);
  } else {
    isDark.value = storedDarkMode === 'true';
  }
  // Aplica a classe "dark" conforme o valor definido
  document.documentElement.classList.toggle('dark', isDark.value);
  
  // Inicializa o clinicStore e aplica as cores baseadas no modo atual
  clinicStore.initialize();
  
  // Busca os dados da clínica da API se o usuário estiver autenticado
  if (isAuthenticated.value) {
    try {
      // Primeiro carrega os dados completos da clínica via clinicService
      await clinicStore.fetchClinicData();
      
      // Depois carrega os estilos específicos
      await clinicStore.fetchClinic();
    } catch (error) {
      console.error('Erro ao carregar dados da clínica:', error);
    }
  }
  
  // Inicializa o relógio
  updateTime();
  timeInterval = setInterval(updateTime, 1000);
});

const handleLogout = () => {
  logout();
};

// Firebase Messaging só será inicializado em HTTPS
if (window.location.protocol === 'https:') {
  import("firebase/messaging").then(({ getMessaging, onMessage }) => {
    const messaging = getMessaging();
    onMessage(messaging, (payload) => {
      console.log('Message received. ', payload);
    });
  }).catch(error => {
    console.error('Erro ao carregar Firebase Messaging:', error);
  });
}

// Detectar clique fora do menu
const handleClickOutside = (event) => {
  // Fecha o menu de usuário ao clicar fora
  if (
    isUserMenuOpen.value &&
    !event.target.closest('[data-user-menu]')
  ) {
    isUserMenuOpen.value = false;
  }

  // Fecha o menu de notificações ao clicar fora
  if (
    isNotificationMenuOpen.value &&
    !event.target.closest('[data-notification-menu]')
  ) {
    isNotificationMenuOpen.value = false;
  }
};

// Detectar clique fora do menu
const handleClickOutsideMobile = (event) => {
  // Fecha o menu de usuário ao clicar fora
  if (
    isMobileUserMenuOpen.value &&
    !event.target.closest('[data-user-menu-mobile]')
  ) {
    isMobileUserMenuOpen.value = false;
  }
};

// Estado para exibir a notificação
const isPopupVisible = ref(false);
const popupMessage = ref({ title: '', body: '' });

// Detecta se é mobile
const isMobile = ref(false);
onMounted(() => {
  isMobile.value = window.innerWidth <= 768; // Define se é mobile
});

// Função para exibir notificação
const showPopup = (title, body) => {
  popupMessage.value = { title, body };
  isPopupVisible.value = true;

  // Fecha automaticamente após 5s
  setTimeout(() => {
    isPopupVisible.value = false;
  }, 5000);
};

// Fecha manualmente
const closePopup = () => {
  isPopupVisible.value = false;
};

// Escuta mensagens do Firebase e exibe pop-up (apenas em HTTPS)
onMounted(() => {
  if (window.location.protocol === 'https:') {
    import("firebase/messaging").then(({ getMessaging, onMessage }) => {
      const messaging = getMessaging();
      onMessage(messaging, (payload) => {
        console.log("Mensagem recebida:", payload);

        const notificationTitle = payload?.notification?.title || "Nova Notificação";
        const notificationBody = payload?.notification?.body || "Você recebeu uma nova mensagem.";

        showPopup(notificationTitle, notificationBody);
      });
    }).catch(error => {
      console.error('Erro ao carregar Firebase Messaging para notificações:', error);
    });
  }
});

const toggleMobileUserMenu = () => {
  isMobileUserMenuOpen.value = !isMobileUserMenuOpen.value;
};

// Alternar menu de usuário
const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value;
  if (isUserMenuOpen.value) {
    isNotificationMenuOpen.value = false; // Fecha notificações se abertas
  }
};

// Registrar eventos de clique
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.title = 'InovaMed - RIS e PACS';
  document.addEventListener('click', handleClickOutsideMobile);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('click', handleClickOutsideMobile);
  
  // Limpa o intervalo do relógio
  if (timeInterval) {
    clearInterval(timeInterval);
  }
});



const navContainer = ref(null);
const showLeftArrow = ref(false);
const showRightArrow = ref(false);
const perfil = usePerfilStore();

const profileImage = computed(() => {
  // Se o perfil foi carregado e a imagem existe e possui formato esperado, retorna-a.
  if (perfil.carregado && perfil.profileImage) {
    if (
      perfil.profileImage.startsWith('data:image') ||
      perfil.profileImage.startsWith('http')
    ) {
      return perfil.profileImage;
    }
  }
  return '/default-avatar.png';
});

// Estado para controlar a rolagem
const isScrolling = ref(false);
let scrollTimeout = null;

// Função para verificar a posição do scroll
const checkScrollPosition = () => {
  if (navContainer.value) {
    const { scrollLeft, scrollWidth, clientWidth } = navContainer.value;

    const isScrollable = scrollWidth > clientWidth + 5; // Margem para precisão

    if (isScrollable) {
      showLeftArrow.value = scrollLeft > 10;
      showRightArrow.value = scrollLeft + clientWidth < scrollWidth - 10;
    } else {
      showLeftArrow.value = false;
      showRightArrow.value = false;
    }
  }
};

// Função para iniciar a rolagem
const startScrolling = () => {
  isScrolling.value = true;
  if (scrollTimeout) clearTimeout(scrollTimeout);
  scrollTimeout = setTimeout(() => {
    isScrolling.value = false;
  }, 300); // Ajuste o tempo conforme necessário
};

// Adiciona evento de scroll e verifica na montagem
onMounted(() => {
  checkScrollPosition();
  if (navContainer.value) {
    navContainer.value.addEventListener('scroll', startScrolling);
    navContainer.value.addEventListener('scroll', checkScrollPosition);
  }
  window.addEventListener('resize', checkScrollPosition); // Recalcula em mudanças de tamanho
});

// Remove eventos na desmontagem
onBeforeUnmount(() => {
  if (navContainer.value) {
    navContainer.value.removeEventListener('scroll', startScrolling);
    navContainer.value.removeEventListener('scroll', checkScrollPosition);
  }
  window.removeEventListener('resize', checkScrollPosition);
});

// Observa mudanças no modo escuro para aplicar cores automaticamente
watch(isDark, () => {
  // Aplica as cores do clinicStore quando o modo mudar
  clinicStore.applyClinicStyles();
});

const isSidebarHovered = ref(false);

// Horário atual de Brasília
const currentTime = ref('');

// Função para atualizar o horário
const updateTime = () => {
  const now = new Date();
  const brasiliaTime = new Date(now.toLocaleString("en-US", {timeZone: "America/Sao_Paulo"}));
  currentTime.value = brasiliaTime.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo'
  });
};

// Atualizar o horário a cada segundo
let timeInterval = null;

// Inicializar o horário quando o componente for montado
onMounted(() => {
  updateTime();
  timeInterval = setInterval(updateTime, 1000);
});

// Limpar o intervalo quando o componente for desmontado
onBeforeUnmount(() => {
  if (timeInterval) {
    clearInterval(timeInterval);
  }
});
</script>

<style>
@tailwind base;
@tailwind components;
@tailwind utilities;

nav .absolute {
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  width: 32px; /* Tamanho fixo para as áreas das setas */
  pointer-events: none; /* Previne bloqueio da rolagem */
}

nav .absolute > div {
  pointer-events: auto; /* Permite clicar nas setas */
}

body {
  @apply antialiased;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* Animação de Fade */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes bounceX {
  0%,
  100% {
    transform: translateX(0);
  }

  50% {
    transform: translateX(-4px);
  }
}

.animate-bounce-x {
  animation: bounceX 1.5s infinite;
}

/* Remove barra de rolagem visível */
.scrollbar-hidden::-webkit-scrollbar {
  display: none;
}

.scrollbar-hidden {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Estilização customizada da barra de rolagem do sidebar */
aside::-webkit-scrollbar {
  width: 6px;
}

aside::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

aside::-webkit-scrollbar-thumb {
  background: var(--clinic-icon-color);
  border-radius: 3px;
  transition: opacity 0.2s ease;
}

aside::-webkit-scrollbar-thumb:hover {
  background: var(--clinic-icon-color);
  opacity: 0.8;
}

aside .flex-1::-webkit-scrollbar {
  width: 6px;
}

aside .flex-1::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

aside .flex-1::-webkit-scrollbar-thumb {
  background: var(--clinic-icon-color);
  border-radius: 3px;
  transition: opacity 0.2s ease;
}

aside .flex-1::-webkit-scrollbar-thumb:hover {
  background: var(--clinic-icon-color);
  opacity: 0.8;
}

/* Para Firefox */
aside {
  scrollbar-width: thin;
  scrollbar-color: var(--clinic-icon-color) rgba(255, 255, 255, 0.1);
}

aside .flex-1 {
  scrollbar-width: thin;
  scrollbar-color: var(--clinic-icon-color) rgba(255, 255, 255, 0.1);
}

/* Fade-in para pop-up */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Deslizamento para notificação estilo push */
.slide-enter-active, .slide-leave-active {
  transition: transform 0.4s ease-out, opacity 0.4s;
}
.slide-enter-from {
  transform: translateY(-100%);
  opacity: 0;
}
.slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.sidebar-scroll {
  overflow-y: scroll;
  scrollbar-width: none;
  /* Firefox */
  -ms-overflow-style: none;
  /* IE and Edge */
}

/* Gradiente nas setas de rolagem */
.bg-gradient-to-r {
  background: linear-gradient(to right, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
}

.bg-gradient-to-l {
  background: linear-gradient(to left, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
}

.sidebar-scroll::-webkit-scrollbar {
  display: none;
  /* Chrome, Safari and Opera */
}

.fade-in {
  animation: fadeIn 0.5s ease-out;
}

.default-avatar {
  background-color: #e5e7eb;
  /* Cor de fundo do avatar genérico */
  color: #6b7280;
  /* Cor do texto no avatar genérico */
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.775rem;
  font-weight: 700;
}

.scrollbar-hidden {
  -ms-overflow-style: none;
  /* IE and Edge */
  scrollbar-width: none;
  /* Firefox */
}

.scrollbar-hidden::-webkit-scrollbar {
  display: none;
  /* Chrome, Safari, and Opera */
}

img.rounded-full,
div.rounded-full {
  height: 2rem;
  /* Altura fixa para recolhido */
  width: 2rem;
  /* Largura fixa para recolhido */
}

@media (min-width: 768px) {
  img.rounded-full:not(.h-10),
  div.rounded-full:not(.h-10) {
    height: 2rem;
    /* Altura para expandido */
    width: 2rem;
    /* Largura para expandido */
  }
}

/* Animação para o balão */
.slide-from-avatar-enter-active {
  transition: transform 0.3s ease-out, opacity 0.3s ease-out;
}

.slide-from-avatar-leave-active {
  transition: transform 0.3s ease-in, opacity 0.2s ease-in;
}

.slide-from-avatar-enter-from {
  transform: scale(0.8) translateY(20px);
  opacity: 0;
}

.slide-from-avatar-enter-to {
  transform: scale(1) translateY(0);
  opacity: 1;
}

.slide-from-avatar-leave-from {
  transform: scale(1);
  opacity: 1;
}

.slide-from-avatar-leave-to {
  transform: scale(0.8) translateY(20px);
  opacity: 0;
}

/* Menu Usuário */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.3s ease-out;
}

button:hover,
.btn-primary:hover {
  background-color: var(--clinic-btn-bg-hover);
  color: #fff;
}

/* Botões principais usando variáveis do clinicStore */
button,
.btn-primary {
  background-color: var(--clinic-btn-bg);
  color: var(--clinic-font-color);
  border: none;
  transition: background 0.2s, color 0.2s;
}
button:hover,
.btn-primary:hover {
  background-color: var(--clinic-btn-bg-hover);
  color: var(--clinic-font-color);
}
button:focus,
.btn-primary:focus {
  outline: 2px solid var(--clinic-btn-bg-hover);
  outline-offset: 2px;
}

/* Botão secundário usando variáveis do clinicStore */
.btn-secondary {
  background-color: var(--clinic-btn-bg-hover);
  color: var(--clinic-font-color);
}
.btn-secondary:hover {
  background-color: var(--clinic-btn-bg);
  color: var(--clinic-font-color);
}

/* Sidebar nav item ativo (usando variáveis do clinicStore) */
.sidebar-active {
  background-color: var(--clinic-btn-bg-hover) !important;
  color: var(--clinic-font-color) !important;
}

.sidebar-active {
  background-color: var(--clinic-btn-bg-hover) !important;
  color: var(--clinic-font-color) !important;
}

:root {
  --clinic-btn-bg: #059669;
  --clinic-icon-color: #059669;
  --clinic-font-color: #22223b;
}

.btn-primary, .btn-save {
  background: var(--clinic-btn-bg) !important;
  color: #fff !important;
}

.icon, .sidebar-icon, .menu-icon {
  color: var(--clinic-icon-color) !important;
}

body, .main-content, .sidebar, .header, .footer {
  color: var(--clinic-font-color);
}

.clinic-btn {
  background: var(--clinic-btn-bg) !important;
  color: #fff !important;
  transition: background 0.2s;
}

.clinic-btn:hover {
  background: var(--clinic-btn-bg-hover) !important;
}

.clinic-text {
  color: var(--clinic-font-color) !important;
}

.clinic-icon {
  color: var(--clinic-icon-color) !important;
}
</style>